<template>
  <VSection v-if="hasBundleUsage" label="transactions.bundle_usage_headline" level="5">
    <transition name="fade" mode="out-in" appear>
      <VMessage v-if="loadOnDemand.msgBox.type" v-bind="loadOnDemand.msgBox"></VMessage>

      <div v-else-if="!loadOnDemand.isLoading" class="row">
        <div v-if="loadOnDemandStorage.bundleUsageData" class="col-l-12 col-m-12">
          <VueGoodTable
            style-class="table table-hover"
            :columns="tableColumnsUsedBundles"
            :rows="getDetailsBundleUsedRows()"
            :paginate="true"
            :rows-per-page-text="$t('common.tablePerPage')"
            :of-text="$t('common.tableOf')"
            :all-text="$t('common.tableAll')"
            :prev-text="$t('common.tablePrev')"
            :next-text="$t('common.tableNext')"
            :line-numbers="false"
          >
            <template #table-column="{ column }">
              <VTableHeader :columns="tableColumnsUsedBundles" :column="column" locale-namespace="transactions" />
            </template>
          </VueGoodTable>
        </div>

        <div v-else class="col-l-12 col-m-12">
          <div v-if="loadOnDemand.errorMessage.length > 0" class="notification notification-negative" role="alert">
            <h3 class="notification-heading">{{ $t('common.headerError') }}</h3>
            <div class="notification-content">
              <br />
              <strong> {{ $t('transactions.msgErrOnReloadHeader') }} </strong>
              <br />
              <br />
              {{ loadOnDemand.errorMessage.toString() }}
            </div>
          </div>

          <VButton
            v-if="
              !loadOnDemand.msgBox.textKey &&
                !loadOnDemandStorage.bundleUsageData &&
                (!loadOnDemand.errorMessage.length > 0 || loadOnDemand.errorCodeType === 'RETRY')
            "
            class="btn btn-default"
            label="transactions.btnShowBundleUsages"
            @click="loadOnDemandBundleUsages"
          />
        </div>
      </div>
    </transition>
  </VSection>
</template>

<script>
import Proxy from '@/proxies/PaymentTransactionsProxy';
import VSection from '@/components/Section';
import VButton from '@/components/Button';
import MessageMixin from '@/mixins/message';
import { VueGoodTable } from 'vue-good-table';
import VTableHeader from '@/components/Table/Header';

const tableColumnsUsedBundles = [
  {
    label: 'transactions.bundle_usage_template_id_column_heading',
    field: 'templateId',
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_usage_bundle_instance_id_column_heading',
    field: 'instanceId',
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_usage_position_type_column_heading',
    field: 'templatePositionTypeDescription',
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_usage_vas_code_column_heading',
    field: 'vasCodes',
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_usage_consumption_column_heading',
    field: 'calcUniqueConsumedVolume' /* appliedVolume */,
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_usage_free_remaining_balance_column_heading',
    field: 'calcUniqueRemainingVolume',
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_usage_remaining_claim_column_heading',
    field: 'calcUniqueRemainingToPayVolume',
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_usage_overall_consumption_column_heading',
    field: 'calcUniqueIncludedVolume' /* totalUsage */,
    sortable: true,
    filterable: false,
  },
];

export default {
  name: 'PaymentTransactionDetailsProductsBundleUsage',

  components: {
    VueGoodTable,
    VSection,
    VButton,
    VMessage: () => import('@/components/Message'),
    VTableHeader,
  },

  mixins: [MessageMixin],

  props: {
    // input data
    transactionDetails: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      tableColumnsUsedBundles,
      loadOnDemand: {
        isLoading: false,
        foundNoData: false,

        errorMessage: '',
        errorCode: '',
        errorCodeType: '',

        msgBox: {
          type: '',
          textKey: '',
        },
      },
      loadOnDemandStorage: {
        bundleUsageData: null,
      },
      currentTransactionId: null,
    };
  },

  computed: {
    hasTransactionDetails() {
      return !!this.transactionDetails;
    },

    isLoadingCompleted() {
      if (!this.loadOnDemand.isLoadingCompleted) {
        return false;
      }
      return this.loadOnDemand.isLoadingCompleted === true;
    },

    hasBundleUsage() {
      if (!this.transactionDetails) {
        return false;
      }
      // if null, undefined "and" false
      if (!this.transactionDetails.flagBundleUsage) {
        return false;
      }
      return this.transactionDetails.flagBundleUsage;
    },
  },

  methods: {
    async loadOnDemandBundleUsages() {
      this.loadOnDemand.isLoading = true;

      this.clearCompMessage(this.loadOnDemand.msgBox);

      this.loadOnDemand.errorMessage = '';
      this.loadOnDemand.errorCodeType = '';
      this.loadOnDemand.errorCode = '';

      this.loadOnDemand.foundNoData = false;

      this.setCompMessage(this.loadOnDemand.msgBox, 'LOADING');
      try {
        const bundleUsageList = await new Proxy({
          partitionIndex: this.transactionDetails.partitionIndex,
        }).bundleUsage(this.transactionDetails.transactionId);
        if (bundleUsageList === null) {
          this.loadOnDemandStorage.bundleUsageData = null; // empty list
          this.loadOnDemand.foundNoData = true;
          this.setCompMessage(this.loadOnDemand.msgBox, 'ERROR', 'transactions.msgErrFoundNoData');
        } else {
          this.loadOnDemandStorage.bundleUsageData = bundleUsageList;
          this.clearCompMessage(this.loadOnDemand.msgBox);
        }
      } catch (error) {
        this.loadOnDemandStorage.bundleUsageData = null;

        this.loadOnDemand.errorCodeType = error?.data?.codeType;
        this.loadOnDemand.errorCode = error?.data?.code;
        this.loadOnDemand.errorMessage = this.loadOnDemand.errorCode ? this.loadOnDemand.errorCode : 'UNKNOWN';

        this.clearCompMessage(this.loadOnDemand.msgBox);

        // this.setCompMessage(this.loadOnDemand.msgBoxBundleUsage, 'ERROR', error);
      }
      this.loadOnDemand.isLoading = false;
    },

    calculateResellingRemainingVolume(resellingIncludedVolume, resellingConsumedVolume) {
      if (resellingIncludedVolume === null || resellingConsumedVolume === null) {
        return '';
      }
      if (resellingIncludedVolume >= resellingConsumedVolume) {
        return (resellingIncludedVolume - resellingConsumedVolume).toString();
      }

      return '0';
    },
    getDetailsBundleUsedRows() {
      if (this.transactionDetails === undefined || this.loadOnDemandStorage === null || this.loadOnDemandStorage.bundleUsageData === null) {
        return [];
      }

      this.loadOnDemandStorage.bundleUsageData.forEach(element => {
        // default settings [undefined check to prevent blinking text on computed cycle]
        if (element.calcUniqueIncludedVolume === undefined) {
          element.calcUniqueIncludedVolume = '';
        }
        if (element.calcUniqueConsumedVolume === undefined) {
          element.calcUniqueConsumedVolume = '';
        }
        if (element.calcUniqueRemainingVolume === undefined) {
          element.calcUniqueRemainingVolume = '';
        }
        if (element.calcUniqueRemainingToPayVolume === undefined) {
          element.calcUniqueRemainingToPayVolume = '';
        }

        // reselling
        if (element.appliedVolume !== null) {
          element.calcUniqueConsumedVolume = element.appliedVolume.toString();
        }
        if (element.includedVolume !== null) {
          element.calcUniqueIncludedVolume = element.includedVolume.toString();
        }

        // remaining
        if (element.templatePositionType !== null) {
          switch (element.templatePositionType) {
            case 0: // type normal = Consumption of a purchased number
              // reselling - volume in units
              // ===========================
              // calculate reselling remainingVolume
              if (element.includedVolume !== null && element.appliedVolume !== null) {
                element.calcUniqueRemainingVolume = this.calculateResellingRemainingVolume(element.includedVolume, element.appliedVolume);
                // check: element.includedToPayVolume should only be set for type = 1 inverse
              }
              break;
            case 1: // type inverse = Extra service if a number of units/amount is reached
              // reselling - volume in units
              // ===========================
              // calculate reselling remainingVolume
              if (element.includedVolume !== null && element.appliedVolume !== null) {
                element.calcUniqueIncludedVolume = element.includedVolume.toString();

                // check: element.includedToPayVolume should only be set for type = 1 inverse
                if (element.includedToPayVolume === null) {
                  element.calcUniqueRemainingVolume = this.calculateResellingRemainingVolume(element.includedVolume, element.appliedVolume);
                } else if (element.includedToPayVolume >= element.appliedVolume) {
                  element.calcUniqueRemainingVolume = element.includedVolume.toString();
                } else if (element.appliedVolume >= element.includedToPayVolume + element.includedVolume) {
                  element.calcUniqueRemainingVolume = (element.appliedVolume - element.includedToPayVolume - element.includedVolume).toString();
                }
                // calculate remainingToPayVolume
                element.calcUniqueRemainingToPayVolume = (element.includedToPayVolume - element.appliedVolume).toString();
              }
              break;
            case 2: // type flatrate
              element.calcUniqueRemainingVolume = this.$t('bundles.bundlePositionTypeFlatrate');
              element.calcUniqueRemainingToPayVolume = this.$t('bundles.bundlePositionTypeFlatrate');
              break;
            default:
              break;
          } // end switch
        } // end if templatePositionType !== null
      }); // for each

      return this.loadOnDemandStorage.bundleUsageData;
    },
  },
};
</script>
